/**
 * Parse subject ID template with support for:
 * - $n, $nn, $nnn... for increment with padding
 * - $& for site ID substitution
 *
 * @param template - Template string (e.g., "10-$&-$nnn")
 * @param siteId - Site ID to substitute for $&
 * @param increment - Increment value to substitute for $n patterns
 * @returns Parsed subject ID string
 *
 * @example
 * parseSubjectIdTemplate("10-$&-$nnn", "101", 1) // "10-101-001"
 * parseSubjectIdTemplate("$&-$nnnn", "102", 5) // "102-0005"
 */
export function parseSubjectIdTemplate(
  template: string,
  siteId: string,
  increment: number
): string {
  let result = template;

  // Ensure increment is a valid number
  const validIncrement = typeof increment === 'number' && !isNaN(increment) ? increment : 1;

  // Replace $& with site ID (only if template contains $&)
  if (result.includes('$&')) {
    result = result.replace(/\$&/g, siteId);
  }

  // Replace $n patterns with padded increment
  // Match $n, $nn, $nnn, etc. (at least one 'n')
  result = result.replace(/\$n+/g, (match) => {
    const paddingLength = match.length - 1; // Subtract 1 for the '$'
    return String(validIncrement).padStart(paddingLength, '0');
  });

  return result;
}

/**
 * Validate subject ID template
 * @param template - Template string to validate
 * @returns Object with isValid flag and error message if invalid
 */
export function validateSubjectIdTemplate(template: string): {
  isValid: boolean;
  error?: string;
} {
  if (!template || template.trim().length === 0) {
    return {
      isValid: false,
      error: 'Template cannot be empty',
    };
  }

  // Check if template contains at least one increment pattern ($n, $nn, etc.)
  const hasIncrementPattern = /\$n+/.test(template);

  if (!hasIncrementPattern) {
    return {
      isValid: false,
      error: 'Template must contain at least one increment pattern ($n, $nn, $nnn, etc.)',
    };
  }

  return { isValid: true };
}

